/**
 * Assumptions Registry — FIE Screen 3
 * Governance-hardened: protected assumptions require elevated permissions, minimum reason length,
 * and explicit confirmation. Locked periods block edits.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Lock, Pencil, ShieldCheck, ShieldAlert, Info } from "lucide-react";
import { useFIE } from "@/data/fie/context";
import { useRole } from "@/data/RoleContext";

import type { Assumption, FIEAuditEntry } from "@/data/fie/types";
import { toast } from "sonner";

const MIN_REASON_LENGTH = 10;

export function AssumptionsRegistryView() {
  const { assumptions, setAssumptions, addAuditEntry, isPeriodLocked, selectedPeriodId, logRestrictedAccess, canEditAssumptions, canEditProtected } = useFIE();
  const { role } = useRole();
  const canEdit = canEditAssumptions;
  const canEditProt = canEditProtected;
  
  const locked = isPeriodLocked(selectedPeriodId);

  const [editingAssumption, setEditingAssumption] = useState<Assumption | null>(null);
  const [newValue, setNewValue] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const categories = [...new Set(assumptions.map(a => a.category))];

  const handleSave = (confirmed?: boolean) => {
    if (!editingAssumption || !newValue || !changeReason) {
      toast.error("All fields required: Enter a new value and reason for the change.");
      return;
    }

    if (changeReason.trim().length < MIN_REASON_LENGTH) {
      toast.error(`Reason too short: Provide at least ${MIN_REASON_LENGTH} characters explaining the change.`);
      return;
    }

    const numVal = parseFloat(newValue);
    if (isNaN(numVal)) {
      toast.error("Invalid value");
      return;
    }

    if (editingAssumption.isProtected && !canEditProtected) {
      logRestrictedAccess("edit_protected_assumption", `U-${role.toUpperCase()}`, role, `Attempted to edit protected assumption: ${editingAssumption.label}`);
      toast.error("Insufficient permissions: Only executive finance roles can modify protected assumptions.");
      return;
    }

    if (editingAssumption.isProtected && !confirmed) {
      return;
    }

    const actionType = editingAssumption.isProtected ? "protected_assumption_changed" : "assumption_changed";

    const auditEntry: FIEAuditEntry = {
      id: `AUD-${Date.now()}`,
      timestamp: new Date().toISOString(),
      userId: `U-${role.toUpperCase()}`,
      userName: role,
      userRole: role,
      action: actionType,
      objectType: "assumption",
      objectId: editingAssumption.id,
      objectLabel: editingAssumption.label,
      oldValue: `${editingAssumption.value}${editingAssumption.unit}`,
      newValue: `${numVal}${editingAssumption.unit}`,
      reason: changeReason,
      effectiveDate: new Date().toISOString().split("T")[0],
      metadata: editingAssumption.isProtected ? { protectedEdit: true, requiresElevatedConfirmation: true } : undefined,
    };

    setAssumptions(prev => prev.map(a =>
      a.id === editingAssumption.id
        ? { ...a, value: numVal, version: a.version + 1, changedBy: role, changedAt: new Date().toISOString().split("T")[0], reason: changeReason }
        : a
    ));

    addAuditEntry(auditEntry);
    toast.success(`${editingAssumption.label} changed to ${numVal}${editingAssumption.unit}`);
    setEditingAssumption(null);
    setNewValue("");
    setChangeReason("");
    setDialogOpen(false);
  };

  const handleProtectedConfirm = () => {
    handleSave(true);
  };

  const handleEditAttemptOnLocked = (a: Assumption) => {
    logRestrictedAccess("edit_locked_assumption", `U-${role.toUpperCase()}`, role, `Attempted to edit assumption "${a.label}" during locked period ${selectedPeriodId}`);
    toast.error("Period is locked: Assumptions cannot be edited for closed or reconciled periods. Changes only apply to active/projected periods.");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Assumptions Registry</h1>
        <p className="text-sm text-muted-foreground">
          Benchmark assumptions used where actual data is unavailable. All changes are versioned and audited.
        </p>
      </div>

      {locked && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-warning/5 border-warning/20">
          <Lock className="h-4 w-4 text-warning" />
          <p className="text-sm text-warning">
            Viewing assumptions for a locked period. These values are frozen from the period's snapshot and cannot be modified.
          </p>
        </div>
      )}

      {!canEdit && !locked && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-warning/5 border-warning/20">
          <Lock className="h-4 w-4 text-warning" />
          <p className="text-sm text-warning">View-only access. Contact an executive finance user to modify assumptions.</p>
        </div>
      )}

      <div className="flex items-start gap-2 p-3 rounded-lg border bg-info/5 border-info/20">
        <Info className="h-4 w-4 text-info mt-0.5 shrink-0" />
        <p className="text-sm text-info">
          Editing assumptions affects only active and projected periods. Historical/closed periods remain bound to their original snapshot.
        </p>
      </div>

      {categories.map(cat => (
        <Card key={cat}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              {cat}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assumption</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Last Changed</TableHead>
                  <TableHead>By</TableHead>
                  {canEdit && <TableHead></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {assumptions.filter(a => a.category === cat).map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {a.label}
                        {a.isProtected && <Lock className="h-3 w-3 text-warning" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{a.description}</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {a.unit === "$" ? `$${a.value}` : `${a.value}${a.unit}`}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px]">v{a.version}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.changedAt}</TableCell>
                    <TableCell className="text-xs">{a.changedBy}</TableCell>
                    {canEdit && (
                      <TableCell>
                        {locked ? (
                          <Button variant="ghost" size="sm" onClick={() => handleEditAttemptOnLocked(a)} title="Period is locked">
                            <Lock className="h-3 w-3" />
                          </Button>
                        ) : a.isProtected && !canEditProtected ? (
                          <Button variant="ghost" size="sm" disabled title="Requires executive finance access">
                            <Lock className="h-3 w-3" />
                          </Button>
                        ) : (
                          <Dialog open={dialogOpen && editingAssumption?.id === a.id} onOpenChange={(open) => { if (!open) { setDialogOpen(false); setEditingAssumption(null); } }}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => { setEditingAssumption(a); setNewValue(String(a.value)); setChangeReason(""); setDialogOpen(true); }}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit: {a.label}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Current Value</p>
                                  <p className="font-mono font-bold">{a.unit === "$" ? `$${a.value}` : `${a.value}${a.unit}`}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">New Value ({a.unit})</label>
                                  <Input type="number" value={newValue} onChange={e => setNewValue(e.target.value)} className="mt-1" />
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Reason for Change * (min {MIN_REASON_LENGTH} chars)</label>
                                  <Textarea value={changeReason} onChange={e => setChangeReason(e.target.value)} placeholder="Explain why this assumption is being changed..." className="mt-1" />
                                  {changeReason.length > 0 && changeReason.trim().length < MIN_REASON_LENGTH && (
                                    <p className="text-xs text-destructive mt-1">{MIN_REASON_LENGTH - changeReason.trim().length} more characters required</p>
                                  )}
                                </div>
                                {a.isProtected && (
                                  <div className="flex items-center gap-2 p-2 rounded border bg-warning/5 border-warning/20">
                                    <ShieldAlert className="h-3 w-3 text-warning" />
                                    <p className="text-xs text-warning">This is a protected assumption. Changes require elevated confirmation and are flagged in audit.</p>
                                  </div>
                                )}
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => { setDialogOpen(false); setEditingAssumption(null); }}>Cancel</Button>
                                {a.isProtected ? (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="destructive" disabled={!newValue || changeReason.trim().length < MIN_REASON_LENGTH}>Modify Protected Assumption</Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Confirm Protected Assumption Change</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          You are modifying <strong>{a.label}</strong>, which is a protected financial assumption. This change will be permanently logged in the audit trail and may affect forecasts for active periods.
                                          <br /><br />
                                          <strong>Old value:</strong> {a.unit === "$" ? `$${a.value}` : `${a.value}${a.unit}`}<br />
                                          <strong>New value:</strong> {a.unit === "$" ? `$${newValue}` : `${newValue}${a.unit}`}<br />
                                          <strong>Reason:</strong> {changeReason}
                                          <br /><br />
                                          <span className="text-destructive font-medium">This will NOT affect locked/closed periods.</span>
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={handleProtectedConfirm}>Confirm Change</AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                ) : (
                                  <Button onClick={() => handleSave(false)} disabled={!newValue || changeReason.trim().length < MIN_REASON_LENGTH}>Save & Log Change</Button>
                                )}
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
